import React, { useState, useEffect } from 'react';
import DownloadAppPopup from '@nykaa/ui-kit/component/DownloadAppPopup';
import styled from '@nykaa/ui-components/styles/styled';
import { getEligibleForVariant } from 'helpers/experimentV2';
import { useSelector } from 'react-redux';
import { State } from 'store/types';
import { DW_APP_BANNER } from 'constants/experiments';
import { NO_AF_URL } from 'layouts/Product/ProductInfo/helper';
import { useIsLoggedIn } from 'hooks/auth';

const BANNER_DISMISSED_KEY = 'nykaa_app_banner_dismissed';
const AF_SMART_SCRIPT_LOADED = 'afSmartScriptLoaded';

const PopupContainer = styled.div`
  position: fixed;
  right: 16px;
  bottom: 90px;
  overflow: hidden;
  z-index: 5;
`;

const PopupContent = styled.div`
  height: 100%;
  width: 100%;
  box-sizing: border-box;
`;

const DownloadAppBanner = () => {
  const [visible, setVisible] = useState(false);
  const [afSmartScriptResult, setAfSmartScriptResult] = useState('');
  const isLoggedIn = useIsLoggedIn();

  const isAfDownloadCtaEnabled = useSelector((state: State) =>
    getEligibleForVariant(state, DW_APP_BANNER, 'A')
  );

  const { backgroundImage, ctaText } =
    useSelector(
      (state: State) => state.remoteConfig.configs?.downloadAppPopup
    ) || {};

  const { iocValue = '0' } = useSelector((state: State) => state.auth.user);

  const showDownloadCTA =
    isAfDownloadCtaEnabled &&
    !!afSmartScriptResult &&
    (iocValue === '1' || !isLoggedIn);

  useEffect(() => {
    const handleAfSmartScriptLoaded = () => {
      const smartScriptResult = window?.AF_SMART_SCRIPT_RESULT || {};
      const { clickURL } = smartScriptResult;
      if (clickURL) {
        setAfSmartScriptResult(clickURL);
      } else if (smartScriptResult === NO_AF_URL) {
        setVisible(false);
      }
    };

    if (isAfDownloadCtaEnabled) {
      window.addEventListener(
        AF_SMART_SCRIPT_LOADED,
        handleAfSmartScriptLoaded,
        {
          passive: true,
        }
      );
      handleAfSmartScriptLoaded();
    }
    return () => {
      if (isAfDownloadCtaEnabled) {
        window.removeEventListener(
          AF_SMART_SCRIPT_LOADED,
          handleAfSmartScriptLoaded
        );
      }
    };
  }, [afSmartScriptResult, isAfDownloadCtaEnabled]);

  useEffect(() => {
    if (showDownloadCTA) {
      const isDismissed =
        sessionStorage.getItem(BANNER_DISMISSED_KEY) === 'true';
      // Only show the banner if it hasn't been dismissed and should be shown
      setVisible(Boolean(showDownloadCTA) && !isDismissed);
    }
  }, [showDownloadCTA, iocValue]);

  const handleClose = () => {
    sessionStorage.setItem(BANNER_DISMISSED_KEY, 'true');
    setVisible(false);
  };

  const handleDownloadClick = () => {
    sessionStorage.setItem(BANNER_DISMISSED_KEY, 'true');
    setVisible(false);
    window.location.href = afSmartScriptResult;
  };

  if (!showDownloadCTA) return null;

  return (
    <>
      {visible && (
        <PopupContainer>
          <PopupContent>
            <DownloadAppPopup
              backgroundImage={backgroundImage}
              ctaText={ctaText}
              onClick={handleDownloadClick}
              onClose={handleClose}
            />
          </PopupContent>
        </PopupContainer>
      )}
    </>
  );
};

export default DownloadAppBanner;
